import API from 'shared/api/API';
import { ORGANIZATION_SERVICE_URL as servicePath } from 'shared/constants/APIconstants';
import { DELETE, GET, PATCH, POST, PUT } from 'shared/utils/Executer';

export const listOrganizations = async () => {
  return API.client.execute(GET, servicePath);
};

export const getById = async id => {
  return API.client.execute(GET, `${servicePath}${id}`);
};

export const updateOrganization = async ({ id, data }) => {
  return API.client.execute(PATCH, `${servicePath}${id}`, data);
};

export const createOrganization = async ({ data }) => {
  return API.client.execute(POST, servicePath, data);
};

/**
 * Owners applied to PolicyTech-created content for the org, populated for display.
 * @returns {Promise<{ data: { owners: Array<{ id: string, email: string, firstName: string, lastName: string }> }, error?: any }>}
 */
export const getPolicyTechOwners = async id => {
  return API.client.execute(GET, `${servicePath}${id}/policytech/owners`);
};

/**
 * Set the org's PolicyTech content owners. Empty array is valid and means
 * "fall back to the system user". Server rejects the whole write (400) if any
 * id isn't an active org member with collaborator-or-higher permission.
 * @returns {Promise<{ data: { ownerIds: string[] }, error?: any }>}
 */
export const setPolicyTechOwners = async ({ id, ownerIds }) => {
  return API.client.execute(PUT, `${servicePath}${id}/policytech/owners`, { ownerIds });
};

/**
 * Zip sources splitting the org's PolicyTech drops into sections.
 * @returns {Promise<{ data: { sources: Array<{ key: string, match: string, siteName: string }> }, error?: any }>}
 */
export const getPolicyTechSources = async id => {
  return API.client.execute(GET, `${servicePath}${id}/policytech/sources`);
};

/**
 * Set the org's PolicyTech zip sources (full replace). Empty array is valid and
 * means single-zip mode. Server rejects the whole write (400) on invalid keys,
 * duplicates, or missing fields.
 * @returns {Promise<{ data: { sources: Array<{ key: string, match: string, siteName: string }> }, error?: any }>}
 */
export const setPolicyTechSources = async ({ id, sources }) => {
  return API.client.execute(PUT, `${servicePath}${id}/policytech/sources`, { sources });
};

class OrganizationAPIPrivate {
  async listOrganizations() {
    return await API.client.execute(GET, servicePath);
  }

  async getById(id) {
    return await API.client.execute(GET, `${servicePath}${id}`);
  }

  async updateOrganization({ id, data }) {
    return await API.client.execute(PATCH, `${servicePath}${id}`, data);
  }

  async createOrganization({ data }) {
    return await API.client.execute(POST, servicePath, data);
  }

  async activateFeature({ feature }) {
    return await API.client.execute(PATCH, `${servicePath}features`, { feature });
  }

  async deactivateFeature({ feature }) {
    return await API.client.execute(DELETE, `${servicePath}features`, { data: { feature } });
  }

  async hubShareAll({ id }) {
    return await API.client.execute(POST, `${servicePath}${id}/hub-share-all`);
  }
}

export default new OrganizationAPIPrivate();
